import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Users, XCircle } from 'lucide-react';
import React from 'react';
export interface TransferResult {
	employeeId: string;
	employeeName?: string | null;
	employeeEmail?: string | null;
	success: boolean;
	reason?: string;
}
interface TransferSummaryProps {
	results: TransferResult[];
	currentManagerEmail: string;
	newManagerEmail: string;
	effectiveDate: string;
}
const TransferSummary: React.FC<TransferSummaryProps> = ({
	results,
	currentManagerEmail,
	newManagerEmail,
	effectiveDate,
}) => {
	const successCount = results.filter((r) => r.success).length;
	const failureCount = results.filter((r) => !r.success).length;
	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Users className="h-5 w-5" />
					Transfer Summary
				</CardTitle>

				{/* Transfer details moved here and displayed horizontally */}
				<div className="flex flex-wrap gap-6 text-sm text-gray-600 py-[10px]">
					<span className="text-slate-500">
						<strong>From:</strong> {currentManagerEmail}
					</span>
					<span className="text-slate-500">
						<strong>To:</strong> {newManagerEmail}
					</span>
					<span className="text-slate-500">
						<strong>Effective Date:</strong> {effectiveDate}
					</span>
				</div>
			</CardHeader>

			<CardContent className="space-y-6">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div className="p-4 text-center border border-gray-300 rounded-xl py-[28px]">
						<h3 className="text-slate-500 text-base font-normal">
							Total Processed
						</h3>
						<p className="text-2xl font-bold text-slate-500">
							{results.length}
						</p>
					</div>
					<div className="p-4 rounded-lg text-center border border-gray-300 py-[28px]">
						<h3 className="text-emerald-600 font-normal text-base">
							Successful
						</h3>
						<p className="text-2xl font-bold text-emerald-600">
							{successCount}
						</p>
					</div>
					<div className="p-4 rounded-lg text-center border border-gray-300 py-[28px]">
						<h3 className="text-red-600 text-base font-normal">
							Failed
						</h3>
						<p className="text-2xl font-bold text-red-600">
							{failureCount}
						</p>
					</div>
				</div>

				<div className="space-y-3">
					<h4 className="font-medium">Results</h4>
					{results.map((result) => (
						<div
							key={result.employeeId}
							className={`p-3 rounded-lg border flex items-center justify-between ${result.success ? 'border-green-200' : 'border-red-200'}`}
						>
							<div className="flex items-center gap-3">
								{result.success ? (
									<CheckCircle className="h-5 w-5 text-green-600" />
								) : (
									<XCircle className="h-5 w-5 text-red-600" />
								)}
								<div>
									<p className="font-medium">
										{result.employeeName}
									</p>
									<p className="text-sm text-gray-600">
										{result.employeeEmail}
									</p>
									{result.reason && (
										<p className="text-sm text-red-600">
											{result.reason}
										</p>
									)}
								</div>
							</div>
							<Badge
								className={
									result.success
										? 'bg-green-100 text-green-800 border-green-200'
										: 'bg-red-100 text-red-800 border-red-200'
								}
								variant="outline"
							>
								{result.success ? 'Updated' : 'Failed'}
							</Badge>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
};
export default TransferSummary;
